/**
 * The workspace service's owned workspace objects (PLAT-13197).
 *
 * Domain classes, deliberately not a reusable framework: the invariants ride the ownership
 * containers rather than state guards. At most one workspace is on screen because it lives in a
 * `DisposableBox`; its settling work and its at-most-one in-flight save cannot outlive it because
 * it owns them in boxes of its own. A stale continuation cannot act because loads and saves only
 * act while their signal is live, and disposing the workspace that owns them aborts it.
 *
 * Disposal is local invalidation, not remote cancellation: a disposed save still completes on the
 * server; its result is simply never applied to whatever replaced the workspace.
 */
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';

export type WorkspaceCloseReason = 'workspace-replaced' | 'service-disposed';

/**
 * One load toward a workspace. The service holds it until it puts its workspace on screen; from
 * then on the {@link LiveWorkspace} it presented owns it until its settling work (widget
 * instantiation, hosted windows, completing research) finishes.
 */
export class WorkspaceLoad implements Disposable {
    readonly #abortController = new AbortController();
    #closeReason: WorkspaceCloseReason = 'workspace-replaced';

    get signal(): AbortSignal {
        return this.#abortController.signal;
    }

    /** Why the load stopped, for hosted windows it opened after being cancelled. */
    get closeReason(): WorkspaceCloseReason {
        return this.#closeReason;
    }

    cancel(reason: WorkspaceCloseReason): void {
        if (this.#abortController.signal.aborted) return;
        this.#closeReason = reason;
        this.#abortController.abort();
    }

    [Symbol.dispose](): void {
        this.cancel('workspace-replaced');
    }
}

/** One in-flight save, owned by the live workspace it saves. */
export class WorkspaceSave implements Disposable {
    readonly #abortController = new AbortController();
    readonly settled: Promise<void>;
    #settle!: () => void;
    #navigationTakenOver = false;

    constructor() {
        this.settled = new Promise<void>((resolve) => {
            this.#settle = resolve;
        });
    }

    get signal(): AbortSignal {
        return this.#abortController.signal;
    }

    /** A navigation waiting on this save leaves the workspace; the save must not redirect it. */
    get navigationTakenOver(): boolean {
        return this.#navigationTakenOver;
    }

    takeOverNavigation(): void {
        this.#navigationTakenOver = true;
    }

    /** Releases everything waiting on {@link settled}; the save has finished, one way or another. */
    finish(): void {
        this.#settle();
    }

    [Symbol.dispose](): void {
        this.#abortController.abort();
        this.#settle();
    }
}

/**
 * The workspace on screen. It owns the load still settling it and at most one in-flight save, so
 * replacing or clearing it stops both. Changes to what it holds are reported through `onChanged`
 * so the service can re-project its load and save states.
 */
export class LiveWorkspace implements Disposable {
    readonly #stack = new DisposableStack();
    readonly #settling: DisposableBox<WorkspaceLoad>;
    readonly #save: DisposableBox<WorkspaceSave>;
    readonly #onChanged: () => void;

    constructor(onChanged: () => void) {
        this.#onChanged = onChanged;
        this.#settling = this.#stack.use(new DisposableBox<WorkspaceLoad>());
        this.#save = this.#stack.use(new DisposableBox<WorkspaceSave>());
    }

    get settling(): boolean {
        return this.#settling.current !== undefined;
    }

    get save(): WorkspaceSave | undefined {
        return this.#save.current;
    }

    /** Takes ownership of the load that presented (or refreshed) this workspace while it settles. */
    settle(load: WorkspaceLoad): void {
        this.#settling.next(load);
        this.#onChanged();
    }

    /** The load finished settling; it no longer needs cancelling. */
    finishSettling(load: WorkspaceLoad): void {
        if (this.#settling.current !== load) return;
        this.#settling.release();
        this.#onChanged();
    }

    /** Starts this workspace's save. Callers refuse a second save while {@link save} is set. */
    beginSave(): WorkspaceSave {
        const save = new WorkspaceSave();
        this.#save.next(save);
        this.#onChanged();
        return save;
    }

    finishSave(save: WorkspaceSave): void {
        save.finish();
        if (this.#save.current !== save) return;
        this.#save.release();
        this.#onChanged();
    }

    /** Stops the settling load with a reason for the windows it may still be opening. */
    cancel(reason: WorkspaceCloseReason): void {
        this.#settling.current?.cancel(reason);
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
